import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { addBreadcrumb, captureException, withScope } from '@sentry/browser';

import { Urls } from 'src/appConstants';
import { useAuthModal } from 'src/modules/auth-modal-store';
import { getEventCreationActionLabel } from 'src/modules/sentry-event-creation';
import { formatSentryError } from 'src/modules/sentry-observability';
import { getSentryPayoutAccountState } from 'src/modules/sentry-payout-account-state';
import { sanitizePayloadForSentry } from 'src/modules/sentry-sanitize';
import {
	SentryAuthApiContext,
	SentryCheckoutApiContext,
	SentryEventManagementApiContext,
	SentryEventCreationApiContext,
	SentryPayoutAccountApiContext,
	SentryPublicEventApiContext,
	getSentryAuthApiContext,
	getSentryCheckoutApiContext,
	getSentryEventManagementApiContext,
	getSentryEventManagementErrorDomain,
	getSentryEventCreationApiContext,
	getSentryEventCreationErrorDomain,
	getSentryPayoutAccountApiContext,
	getSentryPayoutAccountErrorDomain,
	getSentryPublicEventApiContext,
	getSentryPublicEventErrorDomain,
	getSentryRouteContext,
	normalizeApiRoute,
} from 'src/modules/sentry-taxonomy';

type SentryApiMeta = {
	startedAt: number;
	correlationId?: string;
	method: string;
	sanitizedUrl: string;
	routeTemplate: string;
	apiTarget: string;
	requestBodyKeys: string[];
	shouldRecordBreadcrumb: boolean;
	authContext: SentryAuthApiContext | null;
	checkoutContext: SentryCheckoutApiContext | null;
	eventManagementContext: SentryEventManagementApiContext | null;
	eventCreationContext: SentryEventCreationApiContext | null;
	payoutAccountContext: SentryPayoutAccountApiContext | null;
	publicEventContext: SentryPublicEventApiContext | null;
	recipientSurface: string | null;
	apiIssueCaptured?: boolean;
	testScenario?: string;
};

type SentryApiConfig = AxiosRequestConfig & {
	sentryMeta?: SentryApiMeta;
	observabilityTestScenario?: string;
};

type ObservableAxiosError = AxiosError & {
	config: SentryApiConfig;
};

const sanitizeUrl = (url?: string, baseURL?: string) => {
	if (!url) return 'unknown';

	try {
		const fallbackBase =
			typeof window !== 'undefined'
				? window.location.origin
				: 'https://uticket.local';
		const parsedUrl = new URL(url, baseURL ?? fallbackBase);
		const searchKeys = [...parsedUrl.searchParams.keys()].sort();
		const sanitizedPath = `${parsedUrl.pathname}${
			searchKeys.length ? `?${searchKeys.join('&')}` : ''
		}`;

		return /^https?:\/\//i.test(url)
			? `${parsedUrl.origin}${sanitizedPath}`
			: sanitizedPath;
	} catch {
		return url.split('?')[0];
	}
};

const getApiTarget = (baseURL?: string) => {
	if (!baseURL) return 'unknown';

	try {
		return new URL(baseURL).host;
	} catch {
		return baseURL;
	}
};

const isAuthSurfaceActive = () => {
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
	return (
		useAuthModal.getState().isOpen ||
		getSentryRouteContext(pathname).area === 'auth'
	);
};

const getAuthApiContext = ({
	routeTemplate,
	sanitizedUrl,
	method,
	apiTarget,
}: {
	routeTemplate: string;
	sanitizedUrl: string;
	method: string;
	apiTarget: string;
}) =>
	getSentryAuthApiContext({
		routeTemplate,
		sanitizedUrl,
		method,
		isAuthApiTarget: apiTarget === getApiTarget(Urls.AUTH_API),
		isAuthSurface: isAuthSurfaceActive(),
	});

const getCheckoutApiContext = ({
	routeTemplate,
	method,
}: {
	routeTemplate: string;
	method: string;
}) => {
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

	return getSentryCheckoutApiContext({
		routeTemplate,
		method,
		isCheckoutSurface: getSentryRouteContext(pathname).area === 'checkout',
	});
};

const getEventCreationApiContext = ({
	routeTemplate,
	method,
}: {
	routeTemplate: string;
	method: string;
}) => {
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
	const routeContext = getSentryRouteContext(pathname);

	return getSentryEventCreationApiContext({
		routeTemplate,
		method,
		routeContext: routeContext.area === 'event_creation' ? routeContext : null,
	});
};

const getEventManagementApiContext = ({
	routeTemplate,
	method,
}: {
	routeTemplate: string;
	method: string;
}) => {
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
	const routeContext = getSentryRouteContext(pathname);

	return getSentryEventManagementApiContext({
		routeTemplate,
		method,
		routeContext: routeContext.area === 'event_management' ? routeContext : null,
	});
};

const getPublicEventApiContext = ({
	routeTemplate,
	method,
}: {
	routeTemplate: string;
	method: string;
}) => {
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
	const routeContext = getSentryRouteContext(pathname);

	return getSentryPublicEventApiContext({
		routeTemplate,
		method,
		routeContext: routeContext.area === 'event_page' ? routeContext : null,
	});
};

const getPayoutAccountApiContext = ({
	routeTemplate,
	method,
}: {
	routeTemplate: string;
	method: string;
}) => {
	const payoutState = getSentryPayoutAccountState();
	const context = getSentryPayoutAccountApiContext({
		routeTemplate,
		method,
		isPayoutAccountSurface: payoutState.isOpen,
		currentStep: payoutState.step,
	});

	return {
		context,
		recipientSurface: context ? payoutState.surface : null,
	};
};

const getPayloadKeys = (payload: unknown) => {
	if (!payload) return [];

	if (typeof payload === 'string') {
		try {
			const parsedPayload = JSON.parse(payload);
			return typeof parsedPayload === 'object' && parsedPayload !== null
				? Object.keys(parsedPayload).sort()
				: ['string'];
		} catch {
			return ['string'];
		}
	}

	if (Array.isArray(payload)) return ['array'];
	if (typeof payload === 'object') {
		return Object.keys(payload as Record<string, unknown>).sort();
	}

	return [typeof payload];
};

const getSafePayload = (
	payload: unknown,
	eventManagementContext: SentryEventManagementApiContext | null,
	eventCreationContext: SentryEventCreationApiContext | null,
	payoutAccountContext: SentryPayoutAccountApiContext | null,
	publicEventContext: SentryPublicEventApiContext | null
) =>
	eventManagementContext ||
	eventCreationContext ||
	payoutAccountContext ||
	publicEventContext
		? undefined
		: sanitizePayloadForSentry(payload);

const shouldRecordDetailedBreadcrumb = (config: AxiosRequestConfig) => {
	const method = config.method?.toLowerCase() ?? 'get';
	return !['get', 'head', 'options'].includes(method);
};

const addHttpBreadcrumb = ({
	level,
	message,
	data,
}: {
	level: 'info' | 'warning' | 'error';
	message: string;
	data: Record<string, unknown>;
}) => {
	addBreadcrumb({ category: 'http', type: 'http', level, message, data });
};

const isBrowserOffline = () =>
	typeof navigator !== 'undefined' && navigator.onLine === false;

const criticalApiHosts = [Urls.AUTH_API, Urls.CHECKOUT_API, Urls.PAGARME_API]
	.filter((url): url is string => Boolean(url))
	.map(getApiTarget);

const isCriticalApiRoute = (sanitizedUrl: string, apiTarget: string) => {
	if (criticalApiHosts.includes(apiTarget)) return true;

	return (
		/^\/?signin/.test(sanitizedUrl) ||
		/^\/?oauth-signin/.test(sanitizedUrl) ||
		/^\/?signout/.test(sanitizedUrl) ||
		/^\/?sell\/[^/]+\/(buy|pay|edit|close)/.test(sanitizedUrl) ||
		/^\/?sell\/open/.test(sanitizedUrl) ||
		/^\/?wallet/.test(sanitizedUrl)
	);
};

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const SUPPRESSED_ERROR_CODES = new Set(['user_email_duplicated']);

const shouldCaptureApiIssue = ({
	status,
	errorCode,
	sanitizedUrl,
	apiTarget,
	method,
	authContext,
	checkoutContext,
	eventManagementContext,
	eventCreationContext,
	payoutAccountContext,
	publicEventContext,
	testScenario,
}: {
	status?: number;
	errorCode?: string;
	sanitizedUrl: string;
	apiTarget: string;
	method: string;
	authContext: SentryAuthApiContext | null;
	checkoutContext: SentryCheckoutApiContext | null;
	eventManagementContext: SentryEventManagementApiContext | null;
	eventCreationContext: SentryEventCreationApiContext | null;
	payoutAccountContext: SentryPayoutAccountApiContext | null;
	publicEventContext: SentryPublicEventApiContext | null;
	testScenario?: string;
}) => {
	// No Criar Evento, tentativas HTTP são breadcrumbs. A orquestração captura
	// somente a falha terminal; cenários sintéticos continuam abrindo a issue.
	if (eventCreationContext && !authContext && !testScenario) return false;

	const pathname =
		typeof window !== 'undefined' ? window.location.pathname : '/';
	const isEventCreationSurface =
		getSentryRouteContext(pathname).area === 'event_creation';

	// 403 conhecido no wizard (ex.: GET /event/:id sem permissão) = breadcrumb.
	// Defesa quando a taxonomia da API não classificou a rota.
	if (status === 403 && isEventCreationSurface && !testScenario) {
		return false;
	}

	if (!status) {
		return !(
			READ_METHODS.has(method.toUpperCase()) &&
			!authContext &&
			!eventManagementContext &&
			!eventCreationContext &&
			!payoutAccountContext &&
			!publicEventContext
		);
	}

	if (status === 401) return false;
	if (errorCode && SUPPRESSED_ERROR_CODES.has(errorCode)) return false;
	if (status >= 500 || status === 429) return true;

	if (
		(status === 409 || status === 428 || status === 403) &&
		(authContext ||
			checkoutContext ||
			eventManagementContext ||
			eventCreationContext ||
			payoutAccountContext ||
			publicEventContext ||
			isCriticalApiRoute(sanitizedUrl, apiTarget))
	) {
		return true;
	}

	return status === 400 && errorCode === 'unexpected';
};

const getSyntheticEventCreationLabel = (meta: SentryApiMeta): string => {
	if (meta.testScenario === 'event_creation_publish_500') {
		return 'Teste Sintético - Publicação';
	}
	const baseLabel = meta.eventCreationContext
		? getEventCreationActionLabel(
				meta.eventCreationContext.action,
				meta.eventCreationContext.step
			)
		: 'Criar Evento';
	return `Teste Sintético - ${baseLabel}`;
};

const captureApiIssue = ({
	error,
	status,
	errorCode,
	meta,
	responseBodyKeys,
}: {
	error: ObservableAxiosError;
	status?: number;
	errorCode?: string;
	meta: SentryApiMeta;
	responseBodyKeys: string[];
}) => {
	withScope(scope => {
		const offline = isBrowserOffline();
		const eventCreationErrorDomain = meta.eventCreationContext
			? getSentryEventCreationErrorDomain(
					errorCode,
					meta.eventCreationContext.errorDomain
				)
			: null;
		const eventManagementErrorDomain = meta.eventManagementContext
			? getSentryEventManagementErrorDomain(
					errorCode,
					meta.eventManagementContext.errorDomain
				)
			: null;
		const payoutErrorDomain = meta.payoutAccountContext
			? getSentryPayoutAccountErrorDomain(
					errorCode,
					meta.payoutAccountContext.errorDomain
				)
			: null;
		const publicEventErrorDomain = meta.publicEventContext
			? getSentryPublicEventErrorDomain(
					errorCode,
					meta.publicEventContext.errorDomain
				)
			: null;

		scope.setLevel(status && status < 500 ? 'warning' : 'error');
		scope.setTag('error_source', 'api');
		scope.setTag('api_method', meta.method);
		scope.setTag('api_target', meta.apiTarget);
		scope.setTag('api_route_template', meta.routeTemplate);
		scope.setTag('http_status', status ? String(status) : 'network_error');
		scope.setTag('is_api_issue', 'true');
		scope.setTag('offline', String(offline));

		if (meta.authContext) {
			scope.setTag('area', meta.authContext.area);
			scope.setTag('flow', meta.authContext.flow);
			scope.setTag('step', meta.authContext.step);
			scope.setTag('error_domain', 'auth');
			scope.setTag('auth_action', meta.authContext.flow);
		}

		if (meta.checkoutContext) {
			scope.setTag('area', meta.checkoutContext.area);
			scope.setTag('flow', meta.checkoutContext.flow);
			scope.setTag('step', meta.checkoutContext.step);
			scope.setTag('checkout_action', meta.checkoutContext.action);
			scope.setTag('error_domain', meta.checkoutContext.errorDomain);
			if (meta.checkoutContext.authAction) {
				scope.setTag('auth_action', meta.checkoutContext.authAction);
			}
		}

		if (meta.eventCreationContext) {
			scope.setTag('area', meta.eventCreationContext.area);
			scope.setTag('flow', meta.eventCreationContext.flow);
			scope.setTag('step', meta.eventCreationContext.step);
			scope.setTag(
				'event_creation_action',
				meta.eventCreationContext.action
			);
			scope.setTag(
				'error_domain',
				eventCreationErrorDomain ?? meta.eventCreationContext.errorDomain
			);
			if (meta.eventCreationContext.authAction) {
				scope.setTag('auth_action', meta.eventCreationContext.authAction);
			}
		}

		if (meta.eventManagementContext) {
			scope.setTag('area', meta.eventManagementContext.area);
			scope.setTag('flow', meta.eventManagementContext.flow);
			scope.setTag('step', meta.eventManagementContext.step);
			scope.setTag(
				'event_management_action',
				meta.eventManagementContext.action
			);
			scope.setTag(
				'error_domain',
				eventManagementErrorDomain ?? meta.eventManagementContext.errorDomain
			);
		}

		if (meta.payoutAccountContext) {
			scope.setTag('area', meta.payoutAccountContext.area);
			scope.setTag('flow', meta.payoutAccountContext.flow);
			scope.setTag('step', meta.payoutAccountContext.step);
			scope.setTag('payout_action', meta.payoutAccountContext.action);
			scope.setTag(
				'error_domain',
				payoutErrorDomain ?? meta.payoutAccountContext.errorDomain
			);
			if (meta.recipientSurface) {
				scope.setTag('recipient_surface', meta.recipientSurface);
			}
		}

		if (meta.publicEventContext) {
			scope.setTag('area', meta.publicEventContext.area);
			scope.setTag('flow', meta.publicEventContext.flow);
			scope.setTag('step', meta.publicEventContext.step);
			scope.setTag('public_event_action', meta.publicEventContext.action);
			scope.setTag(
				'error_domain',
				publicEventErrorDomain ?? meta.publicEventContext.errorDomain
			);
		}

		if (meta.testScenario) {
			scope.setTag('observability_test', 'true');
			scope.setTag('test_scenario', meta.testScenario);
		}

		if (errorCode) scope.setTag('api_error_code', errorCode);

		scope.setContext('api_error', {
			status: status ?? null,
			method: meta.method,
			url: meta.sanitizedUrl,
			routeTemplate: meta.routeTemplate,
			apiTarget: meta.apiTarget,
			correlationId: meta.correlationId ?? null,
			requestBodyKeys: meta.requestBodyKeys,
			responseBodyKeys,
			requestBody: getSafePayload(
				error.config?.data,
				meta.eventManagementContext,
				meta.eventCreationContext,
				meta.payoutAccountContext,
				meta.publicEventContext
			),
			responseBody: getSafePayload(
				error.response?.data,
				meta.eventManagementContext,
				meta.eventCreationContext,
				meta.payoutAccountContext,
				meta.publicEventContext
			),
			observabilityTestScenario: meta.testScenario ?? null,
			offline,
			checkoutAction: meta.checkoutContext?.action ?? null,
			eventManagementAction: meta.eventManagementContext?.action ?? null,
			eventManagementErrorDomain,
			eventCreationAction: meta.eventCreationContext?.action ?? null,
			errorDomain: meta.checkoutContext?.errorDomain ?? null,
			eventCreationErrorDomain,
			payoutAction: meta.payoutAccountContext?.action ?? null,
			payoutErrorDomain,
			publicEventAction: meta.publicEventContext?.action ?? null,
			publicEventErrorDomain,
			recipientSurface: meta.recipientSurface,
		});

		scope.setFingerprint(
			meta.testScenario
				? ['observability-test', meta.testScenario]
				: [
						'api-error',
						meta.method,
						meta.routeTemplate,
						String(status ?? 'network_error'),
						errorCode ?? 'no-code',
					]
		);

		const isEventCreationSyntheticScenario = Boolean(
			!meta.authContext &&
				meta.eventCreationContext &&
				meta.testScenario?.startsWith('event_creation_')
		);

		const capturedError = isEventCreationSyntheticScenario
			? formatSentryError(error, {
					name: 'Criar Evento',
					context: getSyntheticEventCreationLabel(meta),
				})
			: error;

		captureException(capturedError);
	});
};

const createRequestMeta = (
	config: SentryApiConfig,
	correlationId?: string
): SentryApiMeta => {
	const method = (config.method ?? 'get').toUpperCase();
	const sanitizedUrl = sanitizeUrl(config.url, config.baseURL);
	const apiTarget = getApiTarget(config.baseURL);
	const routeTemplate = normalizeApiRoute(sanitizedUrl, apiTarget);
	const payoutAccount = getPayoutAccountApiContext({ routeTemplate, method });
	const eventManagementContext = getEventManagementApiContext({
		routeTemplate,
		method,
	});
	const publicEventContext = getPublicEventApiContext({
		routeTemplate,
		method,
	});
	const eventCreationContext = getEventCreationApiContext({
		routeTemplate,
		method,
	});

	return {
		startedAt: Date.now(),
		correlationId,
		method,
		sanitizedUrl,
		routeTemplate,
		apiTarget,
		requestBodyKeys: getPayloadKeys(config.data),
		shouldRecordBreadcrumb: shouldRecordDetailedBreadcrumb(config),
		authContext: getAuthApiContext({
			routeTemplate,
			sanitizedUrl,
			method,
			apiTarget,
		}),
		checkoutContext:
			eventManagementContext || publicEventContext
				? null
				: getCheckoutApiContext({ routeTemplate, method }),
		eventManagementContext,
		eventCreationContext,
		payoutAccountContext: payoutAccount.context,
		publicEventContext,
		recipientSurface: payoutAccount.recipientSurface,
		testScenario: config.observabilityTestScenario,
	};
};

export const recordSentryApiRequest = (
	config: AxiosRequestConfig,
	correlationId: string
) => {
	const sentryConfig = config as SentryApiConfig;
	const meta = createRequestMeta(sentryConfig, correlationId);
	sentryConfig.sentryMeta = meta;

	if (
		!meta.shouldRecordBreadcrumb &&
		!meta.eventManagementContext &&
		!meta.publicEventContext
	) {
		return;
	}

	addHttpBreadcrumb({
		level: 'info',
		message: `${meta.method} ${meta.sanitizedUrl}`,
		data: {
			phase: 'request',
			method: meta.method,
			url: meta.sanitizedUrl,
			routeTemplate: meta.routeTemplate,
			apiTarget: meta.apiTarget,
			correlationId,
			requestBodyKeys: meta.requestBodyKeys,
			authArea: meta.authContext?.area,
			authFlow: meta.authContext?.flow,
			authStep: meta.authContext?.step,
			checkoutArea: meta.checkoutContext?.area,
			checkoutFlow: meta.checkoutContext?.flow,
			checkoutStep: meta.checkoutContext?.step,
			checkoutAction: meta.checkoutContext?.action,
			errorDomain: meta.checkoutContext?.errorDomain,
			authAction: meta.checkoutContext?.authAction,
			eventCreationArea: meta.eventCreationContext?.area,
			eventCreationFlow: meta.eventCreationContext?.flow,
			eventCreationStep: meta.eventCreationContext?.step,
			eventCreationAction: meta.eventCreationContext?.action,
			eventCreationErrorDomain: meta.eventCreationContext?.errorDomain,
			eventCreationAuthAction: meta.eventCreationContext?.authAction,
			eventManagementArea: meta.eventManagementContext?.area,
			eventManagementFlow: meta.eventManagementContext?.flow,
			eventManagementStep: meta.eventManagementContext?.step,
			eventManagementAction: meta.eventManagementContext?.action,
			eventManagementErrorDomain: meta.eventManagementContext?.errorDomain,
			payoutArea: meta.payoutAccountContext?.area,
			payoutFlow: meta.payoutAccountContext?.flow,
			payoutStep: meta.payoutAccountContext?.step,
			payoutAction: meta.payoutAccountContext?.action,
			payoutErrorDomain: meta.payoutAccountContext?.errorDomain,
			publicEventArea: meta.publicEventContext?.area,
			publicEventFlow: meta.publicEventContext?.flow,
			publicEventStep: meta.publicEventContext?.step,
			publicEventAction: meta.publicEventContext?.action,
			publicEventErrorDomain: meta.publicEventContext?.errorDomain,
			recipientSurface: meta.recipientSurface,
			testScenario: meta.testScenario,
			requestBody: getSafePayload(
				config.data,
				meta.eventManagementContext,
				meta.eventCreationContext,
				meta.payoutAccountContext,
				meta.publicEventContext
			),
		},
	});
};

export const recordSentryApiResponse = (response: AxiosResponse) => {
	const meta = (response.config as SentryApiConfig).sentryMeta;
	if (
		!meta ||
		(!meta.shouldRecordBreadcrumb &&
			!meta.eventManagementContext &&
			!meta.publicEventContext)
	) {
		return;
	}

	addHttpBreadcrumb({
		level: 'info',
		message: `${meta.method} ${meta.sanitizedUrl} -> ${response.status}`,
		data: {
			phase: 'response',
			method: meta.method,
			url: meta.sanitizedUrl,
			routeTemplate: meta.routeTemplate,
			apiTarget: meta.apiTarget,
			status: response.status,
			durationMs: Date.now() - meta.startedAt,
			correlationId: meta.correlationId,
			checkoutArea: meta.checkoutContext?.area,
			checkoutFlow: meta.checkoutContext?.flow,
			checkoutStep: meta.checkoutContext?.step,
			checkoutAction: meta.checkoutContext?.action,
			errorDomain: meta.checkoutContext?.errorDomain,
			authAction: meta.checkoutContext?.authAction,
			eventCreationArea: meta.eventCreationContext?.area,
			eventCreationFlow: meta.eventCreationContext?.flow,
			eventCreationStep: meta.eventCreationContext?.step,
			eventCreationAction: meta.eventCreationContext?.action,
			eventCreationErrorDomain: meta.eventCreationContext?.errorDomain,
			eventCreationAuthAction: meta.eventCreationContext?.authAction,
			eventManagementArea: meta.eventManagementContext?.area,
			eventManagementFlow: meta.eventManagementContext?.flow,
			eventManagementStep: meta.eventManagementContext?.step,
			eventManagementAction: meta.eventManagementContext?.action,
			eventManagementErrorDomain: meta.eventManagementContext?.errorDomain,
			payoutArea: meta.payoutAccountContext?.area,
			payoutFlow: meta.payoutAccountContext?.flow,
			payoutStep: meta.payoutAccountContext?.step,
			payoutAction: meta.payoutAccountContext?.action,
			payoutErrorDomain: meta.payoutAccountContext?.errorDomain,
			publicEventArea: meta.publicEventContext?.area,
			publicEventFlow: meta.publicEventContext?.flow,
			publicEventStep: meta.publicEventContext?.step,
			publicEventAction: meta.publicEventContext?.action,
			publicEventErrorDomain: meta.publicEventContext?.errorDomain,
			recipientSurface: meta.recipientSurface,
			responseBodyKeys: getPayloadKeys(response.data),
			responseBody: getSafePayload(
				response.data,
				meta.eventManagementContext,
				meta.eventCreationContext,
				meta.payoutAccountContext,
				meta.publicEventContext
			),
		},
	});
};

export const recordSentryApiError = (axiosError: AxiosError) => {
	const error = axiosError as ObservableAxiosError;
	const existingMeta = error.config?.sentryMeta;
	const meta = existingMeta ?? createRequestMeta(error.config ?? {});
	const status = error.response?.status;
	const errorCode = error.response?.data?.code;
	const responseBodyKeys = getPayloadKeys(error.response?.data);
	const shouldAttachPayloads =
		meta.shouldRecordBreadcrumb ||
		Boolean(meta.eventManagementContext) ||
		(status ?? 0) >= 400 ||
		!status ||
		Boolean(meta.publicEventContext) ||
		isCriticalApiRoute(meta.sanitizedUrl, meta.apiTarget);
	const eventCreationErrorDomain = meta.eventCreationContext
		? getSentryEventCreationErrorDomain(
				errorCode,
				meta.eventCreationContext.errorDomain
			)
		: undefined;
	const eventManagementErrorDomain = meta.eventManagementContext
		? getSentryEventManagementErrorDomain(
				errorCode,
				meta.eventManagementContext.errorDomain
			)
		: undefined;
	const payoutErrorDomain = meta.payoutAccountContext
		? getSentryPayoutAccountErrorDomain(
				errorCode,
				meta.payoutAccountContext.errorDomain
			)
		: undefined;
	const publicEventErrorDomain = meta.publicEventContext
		? getSentryPublicEventErrorDomain(
				errorCode,
				meta.publicEventContext.errorDomain
			)
		: undefined;

	if (shouldAttachPayloads) {
		addHttpBreadcrumb({
			level: !status || status >= 500 ? 'error' : 'warning',
			message: `${meta.method} ${meta.sanitizedUrl} -> ${
				status ?? 'network_error'
			}`,
			data: {
				phase: 'response_error',
				method: meta.method,
				url: meta.sanitizedUrl,
				routeTemplate: meta.routeTemplate,
				apiTarget: meta.apiTarget,
				status,
				errorCode,
				durationMs: existingMeta
					? Date.now() - meta.startedAt
					: undefined,
				correlationId: existingMeta?.correlationId,
				requestBodyKeys: meta.requestBodyKeys,
				responseBodyKeys,
				authArea: meta.authContext?.area,
				authFlow: meta.authContext?.flow,
				authStep: meta.authContext?.step,
				checkoutArea: meta.checkoutContext?.area,
				checkoutFlow: meta.checkoutContext?.flow,
				checkoutStep: meta.checkoutContext?.step,
				checkoutAction: meta.checkoutContext?.action,
				errorDomain: meta.checkoutContext?.errorDomain,
				authAction: meta.checkoutContext?.authAction,
				eventCreationArea: meta.eventCreationContext?.area,
				eventCreationFlow: meta.eventCreationContext?.flow,
				eventCreationStep: meta.eventCreationContext?.step,
				eventCreationAction: meta.eventCreationContext?.action,
				eventCreationErrorDomain,
				eventCreationAuthAction: meta.eventCreationContext?.authAction,
				eventManagementArea: meta.eventManagementContext?.area,
				eventManagementFlow: meta.eventManagementContext?.flow,
				eventManagementStep: meta.eventManagementContext?.step,
				eventManagementAction: meta.eventManagementContext?.action,
				eventManagementErrorDomain,
				payoutArea: meta.payoutAccountContext?.area,
				payoutFlow: meta.payoutAccountContext?.flow,
				payoutStep: meta.payoutAccountContext?.step,
				payoutAction: meta.payoutAccountContext?.action,
				payoutErrorDomain,
				publicEventArea: meta.publicEventContext?.area,
				publicEventFlow: meta.publicEventContext?.flow,
				publicEventStep: meta.publicEventContext?.step,
				publicEventAction: meta.publicEventContext?.action,
				publicEventErrorDomain,
				recipientSurface: meta.recipientSurface,
				testScenario: meta.testScenario,
				offline: isBrowserOffline(),
				requestBody: getSafePayload(
					error.config?.data,
					meta.eventManagementContext,
					meta.eventCreationContext,
					meta.payoutAccountContext,
					meta.publicEventContext
				),
				responseBody: getSafePayload(
					error.response?.data,
					meta.eventManagementContext,
					meta.eventCreationContext,
					meta.payoutAccountContext,
					meta.publicEventContext
				),
			},
		});
	}

	const shouldCapture = shouldCaptureApiIssue({
		status,
		errorCode,
		sanitizedUrl: meta.sanitizedUrl,
		apiTarget: meta.apiTarget,
		method: meta.method,
		authContext: meta.authContext,
		checkoutContext: meta.checkoutContext,
		eventManagementContext: meta.eventManagementContext,
		eventCreationContext: meta.eventCreationContext,
		payoutAccountContext: meta.payoutAccountContext,
		publicEventContext: meta.publicEventContext,
		testScenario: meta.testScenario,
	});

	if (existingMeta) existingMeta.apiIssueCaptured = shouldCapture;
	if (shouldCapture) {
		captureApiIssue({ error, status, errorCode, meta, responseBodyKeys });
	}
};
